// src/utils/attribution.js
//
// R31.7e (paid-traffic launch wiring): centralized attribution capture.
// Reads tracking params off the URL, merges with the existing attribution
// blob in localStorage, and persists for the audit form to ship at
// submission time. Closes the loop from ad creative → buyer → $149
// purchase 30 days later.
//
// Designed to be called from EVERY landing page mount (PM, dental, RE,
// HVAC) AND the audit form, so a buyer who lands on /pm?gclid=abc and
// then clicks through to /audit still has the gclid on their submission
// — even though /audit's own URL no longer has the param.
//
// Never throws — localStorage can be disabled (private mode, ad blockers)
// and we'd rather lose attribution than break the funnel.

const KEYS_TO_CAPTURE = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  // Google Ads click ID — single most important field for paid-search
  // attribution. Google Ads conversion-import joins on gclid, NOT utm_*.
  "gclid",
  // Meta / Facebook click ID — enables Meta CAPI server-side conversions
  // when we add Meta Ads later.
  "fbclid",
  // Microsoft / Bing equivalent.
  "msclkid",
];

/**
 * Capture attribution params from the current URL into localStorage.
 * Idempotent — re-running on the same URL is safe. Merges into an
 * existing blob without clobbering earlier-captured fields with empty
 * values (a buyer who arrived via gclid yesterday and hits a clean URL
 * today should keep their gclid on submit).
 *
 * Returns the merged blob for callers that want to log/inspect.
 */
export const captureAttribution = () => {
  if (typeof window === "undefined") return null;
  let captured = {};
  try {
    const sp = new URLSearchParams(window.location.search);
    KEYS_TO_CAPTURE.forEach((k) => {
      const v = sp.get(k);
      if (v) captured[k] = v.slice(0, 200);
    });
  } catch {
    // URL parse failed — nothing to capture this round.
    return null;
  }

  // document.referrer is useful for organic / partner-link attribution
  // when there's no explicit utm/gclid tag.
  if (typeof document !== "undefined" && document.referrer) {
    captured.referrer = document.referrer.slice(0, 500);
  }

  // landing_page = the path the buyer first hit on aiworkshopllc.com.
  // Lets the analytics layer answer "did /pm convert better than
  // /property-management" without needing UTM tagging on every link.
  if (typeof window !== "undefined" && window.location?.pathname) {
    // Only set on first capture so a buyer who lands on /pm and then
    // navigates to /audit doesn't overwrite the original landing path.
    captured.landing_page = window.location.pathname.slice(0, 200);
  }

  if (Object.keys(captured).length === 0) return null;

  try {
    const existing = JSON.parse(localStorage.getItem("attribution") || "{}");
    // R46 (Maya R2 verdict): a NEW paid-click ID (gclid / fbclid /
    // msclkid) signals a fresh ad-click attribution event — yesterday's
    // utm_content / landing_page should NOT bleed into today's
    // dashboards. When any paid-click ID arrives AND differs from the
    // stored one, treat this as a fresh first-touch: wipe stale fields,
    // overwrite captured_at, set landing_page from the current path.
    // Without this, Maya's R2 walk showed landing_page:"/pm" +
    // utm_content:"fcra_screening" surviving from a prior PM walk
    // when she arrived via plain /audit. Corrupts attribution
    // dashboards downstream.
    const _PAID_CLICK_IDS = ["gclid", "fbclid", "msclkid"];
    const _newPaidClick = _PAID_CLICK_IDS.find(
      (k) => captured[k] && captured[k] !== existing[k],
    );
    const merged = _newPaidClick ? {} : { ...existing };
    for (const [k, v] of Object.entries(captured)) {
      // Preserve first-touch landing_page only when this is a
      // continuation visit (no new paid-click). A new paid-click
      // resets landing_page to the current path.
      if (k === "landing_page" && existing.landing_page && !_newPaidClick) continue;
      if (v) merged[k] = v;
    }
    merged.captured_at = _newPaidClick
      ? new Date().toISOString()
      : (merged.captured_at || new Date().toISOString());
    merged.last_seen_at = new Date().toISOString();
    localStorage.setItem("attribution", JSON.stringify(merged));
    return merged;
  } catch {
    // localStorage disabled — best-effort fallback: stash in
    // sessionStorage so at least the same-tab audit submission
    // still ships the data.
    try {
      sessionStorage.setItem("attribution", JSON.stringify(captured));
    } catch { /* both storage layers blocked — attribution is lost */ }
    return captured;
  }
};

/**
 * Read the latest attribution blob from storage for use in the audit
 * submission payload. Returns an empty object if no attribution is
 * available — callers can spread it into their request body
 * unconditionally.
 */
export const readAttribution = () => {
  try {
    const raw = localStorage.getItem("attribution") || sessionStorage.getItem("attribution");
    if (!raw) return {};
    return JSON.parse(raw) || {};
  } catch {
    return {};
  }
};
